import Navbar from "@components/Navbar/Navbar";
import SaveIcon from "@mui/icons-material/Save";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import useAds from "@hooks/useAds";
import useDefaultValues from "@hooks/useDefaultValues";
import { extractDriveId } from "@components/SortableItem/helper";
import useUser from "@hooks/useUser";
import { ctaOptions } from "./constants";
import { notify } from "@utils/notify";
import useRQAdLaunches from "@hooks/useRQAdLaunches";
import { Loader } from "@components/Loader/loader";
import { useNavigate } from "react-router";
import { TextField, FormControl } from "@mui/material";
import { Copy } from "lucide-react";
import SelectDropdown from "@components/SelectDropdown.jsx/SelectDropdown";
import { fetchConnectedPages } from "@services/getConnectedPages";
import AdPostingValidation from "@components/AdPostingValidation/AdPostingValidation";
import { fetchAdsLaunchValidationErrors } from "@services/validateFbAdsLaunch";

const ReviewDefaultValuesAP = () => {
  const userId = localStorage.getItem("userId");
  const storedAdAccountId = localStorage.getItem("adAccountId");
  const storedAdAccountName = localStorage.getItem("adAccountName");
  const fbCampaignType = localStorage.getItem("campaignObjective");
  const oneAdPerAdSet = localStorage.getItem("oneAdPerAdSet") === "true" ? true : false;
  const storedFacebookPageId = localStorage.getItem("selectedFacebookPageId");
  const storedInstagramPageId = localStorage.getItem("selectedInstagramPageId");

  // states
  const [reviewValues, setReviewValues] = useState({});
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [isValuesInitialized, setIsValuesInitialized] = useState(false);
  const [enabledAds, setEnabledAds] = useState({});
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isScheduleLoading, setIsScheduleLoading] = useState(false);
  const [scheduledDateForModal, setScheduledDateForModal] = useState(null);
  const [facebookPages, setFacebookPages] = useState([]);
  const [instagramPages, setInstagramPages] = useState([]);
  const [showInstagramWarning, setShowInstagramWarning] = useState({});

  // Add state for validation modal
  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [isValidating, setIsValidating] = useState(false);

  // add state for publish validation modal
  const [isPublishValidationModalOpen, setIsPublishValidationModalOpen] = useState(false);
  const [publishValidationErrors, setPublishValidationErrors] = useState([]);
  const [isPublishValidating, setIsPublishValidating] = useState(false);

  // hooks
  const { handleGetAdsWithCreatives } = useAds();
  const { handleGetDefaultValues } = useDefaultValues();
  const { handleGetUserById } = useUser();
  const navigate = useNavigate();
  const { handleSaveAdLaunches } = useRQAdLaunches();

  // selectors
  const { ads = [] } = useSelector((state) => state.ads) || {};
  const { defaultValues } = useSelector((state) => state.defaultValues) || {};
  const { user } = useSelector((state) => state.user) || {};
  const { createAdCreativeLoading } = useSelector(
    (state) => state.facebookAdCreatives
  ) || {};
  const { adAccountId } = user || {};

  // Fetch connected pages data using new API
  const getConnectedPagesData = async () => {
    try {
      const response = await fetchConnectedPages({
        user_id: userId,
        force_refresh: false
      });
      if (response?.data?.data?.facebook_pages) {
        setFacebookPages(response.data.data.facebook_pages);
      }
      if (response?.data?.data?.instagram_pages) {
        setInstagramPages(response.data.data.instagram_pages);
      }
    } catch (error) {
      console.error("Error fetching connected pages:", error);
    }
  };

  const handleReviewValuesChange = (adId, e) => {
    const { name, value } = e.target;
    setReviewValues((prev) => {
      const adValues = prev[adId] || {};
      if (name === "deepLink") {
        return {
          ...prev,
          [adId]: {
            ...adValues,
            [name]: value,
          },
        };
      }
      if (name === "facebookPage") {
        const selectedFbPage = facebookPages.find((page) => page.page_id === value);
        // Find matching Instagram account by page_id relation (new API structure)
        let associatedInstaPage = instagramPages.find(
          (insta) => insta.page_id === selectedFbPage?.page_id
        );
        // If not found by direct relation, try matching by name
        if (!associatedInstaPage && selectedFbPage) {
          // Try exact name match first
          associatedInstaPage = instagramPages.find(
            (insta) => insta.insta_page_name === selectedFbPage.name
          );
          // If still not found, try fuzzy matching with the formatted name
          if (!associatedInstaPage) {
            const formattedName = `IGPage_${selectedFbPage.name}`;
            associatedInstaPage = instagramPages.find(
              (insta) => `IGPage_${insta.name}` === formattedName || insta.insta_page_name === formattedName
            );
          }
        }
        // Get a clean list of Instagram options
        const instaOptions = getUniqueOptions();
        // Find the associated account in our clean options
        let matchingInstaOption = null;
        if (associatedInstaPage) {
          matchingInstaOption = instaOptions.find(opt => opt.id === associatedInstaPage.insta_id);
        }
        // Clear warning for this ad when Facebook page changes
        setShowInstagramWarning(prev => ({
          ...prev,
          [adId]: false
        }));
        // Show warning if no linked Instagram page is found
        if (!associatedInstaPage) {
          notify("No Instagram page linked to selected Facebook page", "warning");
        }
        return {
          ...prev,
          [adId]: {
            ...adValues,
            [name]: { id: value },
            instagramPage: matchingInstaOption
              ? { id: matchingInstaOption.id }
              : { id: '' },
          },
        };
      }
      if (name === "instagramPage") {
        // Show warning if selected Instagram page is not linked to selected Facebook page
        const selectedInstaPage = instagramPages.find(page => page.insta_id === value);
        const currentFbPageId = adValues.facebookPage?.id;
        if (selectedInstaPage && selectedInstaPage.page_id !== currentFbPageId) {
          setShowInstagramWarning(prev => ({
            ...prev,
            [adId]: true
          }));
          notify("This Instagram page is not linked with the selected Facebook page", "error");
        } else {
          setShowInstagramWarning(prev => ({
            ...prev,
            [adId]: false
          }));
        }
      }
      return {
        ...prev,
        [adId]: {
          ...adValues,
          [name]: { id: value },
        },
      };
    });
  };

  const prepareAdLaunchRequest = () => {
    if (!ads || !defaultValues || !reviewValues) {
      notify("Missing required data for ad launch", "error");
      return null;
    }

    // Check if we have payload from advanced mapping
    const publishPayloadFromAdvMapping = localStorage.getItem("publishPayloadFromAdvMapping");
    
    if (publishPayloadFromAdvMapping) {
      // If we have advanced mapping data, use that as the base
      try {
        const advMappingPayload = JSON.parse(publishPayloadFromAdvMapping);
        
        if (advMappingPayload && advMappingPayload.adObjects && advMappingPayload.adObjects.length > 0) {
          // Merge the review values with the advanced mapping payload
          const adObjects = advMappingPayload.adObjects.map(adObj => {
            const adReview = reviewValues[adObj.adId] || {};
            
            return {
              ...adObj,
              fbPageId: adReview?.facebookPage?.id || adObj.fbPageId || "",
              fbCallToAction: adReview?.callToAction?.id || "",
              fbInstagramId: adReview?.instagramPage?.id || adObj.fbInstagramId || "",
              trackingTemplate: adReview?.trackingTemplate || "",
              deepLink: adReview?.deepLink || "",
              fbAdLiveStatus: enabledAds[adObj.adId] === true ? "ACTIVE" : "PAUSED",
              oneAdPerAdset: oneAdPerAdSet
            };
          });
          
          return { adObjects };
        }
      } catch (error) {
        console.error("Error parsing advanced mapping payload:", error);
        notify("Error processing advanced mapping data", "error");
      }
    }
    
    // If no advanced mapping data or parsing failed, fall back to the original implementation
    const adObjects = ads.map((ad) => {
      const adReview = reviewValues[ad._id] || {};
      return {
        adId: ad._id,
        fbAdsetId: defaultValues?.targetAdSetId || "",
        fbAdsetName: defaultValues?.fbAdsetName || "",
        fbPageId: adReview?.facebookPage?.id || "",
        fbCallToAction: adReview?.callToAction?.id || "",
        fbInstagramId: adReview?.instagramPage?.id || "",
        fbAdAccountId: storedAdAccountId || adAccountId,
        fbCampaignName: defaultValues?.fbCampaignName || "",
        fbCampaignId: defaultValues?.targetCampaignId || "",
        userId: userId,
        oneAdPerAdset: oneAdPerAdSet,
        fbCampaignType: fbCampaignType || "OUTCOME_SALES",
        trackingTemplate: adReview?.trackingTemplate || "",
        deepLink: adReview?.deepLink || "",
        fbAdLiveStatus: enabledAds[ad._id] === true ? "ACTIVE" : "PAUSED"
      };
    });
    
    return { adObjects };
  };

  const handleSaveAndPublish = async (validate=false) => {
    const payload = prepareAdLaunchRequest();
    if (!payload) {
      notify("Failed to prepare ad launch data", "error");
      return;
    }
    if (validate) {
      // Always check for validation errors before publishing
      const errors = await checkForValidationErrors(payload);
      if (errors.length > 0) {
        setPublishValidationErrors(errors);
        setIsPublishValidationModalOpen(true);
        return;
      }
    }

    // If no errors, proceed with save and publish
    const userConfirmed = window.confirm("Are you sure you want to publish?");
    if (userConfirmed) {
      setIsDataLoading(true);
      try {
        const response = await handleSaveAdLaunches(payload);
        if (response && response.batch_id) {
          navigate("/ads-progress", {
            state: {
              batchId: response.batch_id,
              launches: response.launches,
            },
          });
        } else {
          notify("Failed to create ad launches. Please try again.", "error");
        }
      } catch (error) {
        console.error("Error during ad launch:", error);
        notify("An error occurred while creating ad launches", "error");
      } finally {
        setIsDataLoading(false);
      }
    }
  };

  useEffect(() => {
    setIsDataLoading(true);
    setIsValuesInitialized(false);
    handleGetUserById(userId);
    getConnectedPagesData();
  }, []);

  useEffect(() => {
    if (userId) {
      handleGetDefaultValues(userId, storedAdAccountId);
    }
  }, [userId, storedAdAccountId]);

  useEffect(() => {
    if (userId && defaultValues?.creativeLocationId) {
      handleGetAdsWithCreatives(userId, defaultValues?.creativeLocationId);
    }
  }, [userId, defaultValues]);

  useEffect(() => {
    if (!defaultValues || !ads || !ads.length) return;
    // Facebook Page selection logic (only localStorage and API data)
    let facebookPage = "";
    if (storedFacebookPageId && facebookPages && facebookPages.length > 0) {
      const matchedFbAccount = facebookPages.find(
        (page) => page.page_id === storedFacebookPageId
      );
      facebookPage = matchedFbAccount?.page_id || "";
    }
    // Instagram Page selection logic: match page_id with selected fb page, else empty
    let instagramPage = "";
    if (facebookPage && instagramPages && instagramPages.length > 0) {
      const matchedInsta = instagramPages.find(
        (page) => page.page_id === facebookPage
      );
      instagramPage = matchedInsta?.insta_id || "";
      if (!matchedInsta) {
        notify("No Instagram page linked to selected Facebook page", "warning");
      }
    }
    const callToAction =
      ctaOptions.find(
        (option) => option.text === defaultValues?.callToAction
      ) || (ctaOptions && ctaOptions.length > 0 ? ctaOptions[0] : { id: "" });
    const initialValues = {};
    ads.forEach((ad) => {
      if (!ad || !ad._id) return;
      initialValues[ad._id] = {
        callToAction: callToAction ? { id: callToAction.id } : { id: "" },
        deepLink: "",
        facebookPage: facebookPage ? { id: facebookPage } : { id: "" },
        instagramPage: instagramPage ? { id: instagramPage } : { id: "" },
      };
    });
    const initialEnabledState = {};
    ads.forEach((ad) => {
      if (ad && ad._id) {
        initialEnabledState[ad._id] = false;
      }
    });
    setEnabledAds(initialEnabledState);
    setReviewValues(initialValues);
    setIsValuesInitialized(true);
  }, [defaultValues, ads, facebookPages, instagramPages]);

  useEffect(() => {
    const hasAds = ads && ads.length > 0;
    const hasDefaultValues = !!defaultValues;
    const hasFacebookPages = facebookPages && facebookPages.length > 0;
    const hasInstagramPages = instagramPages && instagramPages.length > 0;
    const allValuesInitialized = isValuesInitialized;
    if (
      hasAds &&
      hasDefaultValues &&
      hasFacebookPages &&
      hasInstagramPages &&
      allValuesInitialized
    ) {
      setIsDataLoading(false);
    }
  }, [ads, defaultValues, facebookPages, instagramPages, isValuesInitialized]);

  // Add validation check function
  const checkForValidationErrors = async (schedulePayload) => {
    try {
      setIsValidating(true);
      const response = await fetchAdsLaunchValidationErrors(schedulePayload);

      if (response.data && response.data.validation && !response.data.validation.is_valid) {
        const errors = response.data.validation.error.map((error, index) => ({
          id: index + 1,
          error: error,
          errorDetails: response.data.validation.error_details[index] || "",
          action: response.data.validation.actions[index] || ""
        }));
        return errors;
      }

      return [];
    } catch (error) {
      console.error("Error validating ads launch:", error);
      notify("Error validating ads launch", "error");
      return [];
    } finally {
      setIsValidating(false);
    }
  };

  const handleScheduleAds = async () => {
    // Prepare the request with the correct format
    const payload = prepareAdLaunchRequest();
    if (!payload) {
      notify("Failed to prepare ad launch data", "error");
      return;
    }

    const schedulePayload = {
      adObjects: payload.adObjects.map(adObject => ({
        ...adObject,
        fbAdLiveStatus: "ACTIVE"
      }))
    };

    // First, check for validation errors using the API
    const errors = await checkForValidationErrors(schedulePayload);

    if (errors.length > 0) {
      // Show validation modal if there are errors
      setValidationErrors(errors);
      setIsValidationModalOpen(true);
      return;
    }

    // If no errors, proceed with original scheduling logic
    const userConfirmed = window.confirm("Are you sure you want to schedule these ads?");
    if (userConfirmed) {
      const scheduledDate = new Date();
      setScheduledDateForModal(scheduledDate);
      setIsScheduleModalOpen(true);
    }
  };

  // Handle validation modal actions
  const handleValidationEditAds = () => {
    setIsValidationModalOpen(false);

    // Proceed with scheduling despite validation errors
    const userConfirmed = window.confirm("Are you sure you want to schedule these ads?");
    if (userConfirmed) {
      const scheduledDate = new Date();
      setScheduledDateForModal(scheduledDate);
      setIsScheduleModalOpen(true);
    }
  };

  const handlePublishValidationEditAds = () => {
    setIsPublishValidationModalOpen(false);
    handleSaveAndPublish(false);
  };

  const handleValidationResolve = async () => {
    setIsValidationModalOpen(false);

    // Prepare the same payload for re-validation
    const payload = prepareAdLaunchRequest();

    // Re-check validation and proceed if resolved
    const errors = await checkForValidationErrors(payload);
    if (errors.length === 0) {
      // If no more errors, proceed with scheduling
      const userConfirmed = window.confirm("Are you sure you want to schedule these ads?");
      if (userConfirmed) {
        const scheduledDate = new Date();
        setScheduledDateForModal(scheduledDate);
        setIsScheduleModalOpen(true);
      }
    } else {
      // If errors still exist, show the validation modal again
      setValidationErrors(errors);
      setIsValidationModalOpen(true);
    }
  };

  const handlePublishValidationResolve = async () => {
    setIsPublishValidationModalOpen(false);
    handleSaveAndPublish(true);
  };

  const handleScheduleDateConfirm = async (scheduledDateTime) => {
    if (isNaN(scheduledDateTime.getTime()) || scheduledDateTime <= new Date()) {
      notify("Please enter a valid future date and time", "error");
      return;
    }

    setIsScheduleLoading(true);

    const minutesUntilLaunch = Math.ceil((scheduledDateTime.getTime() - Date.now()) / 60000);
    const payload = prepareAdLaunchRequest();
    if (!payload) {
      notify("Failed to prepare ad launch data", "error");
      setIsScheduleLoading(false);
      return;
    }
    const schedulePayload = {
      adObjects: payload.adObjects.map(adObject => ({
        ...adObject,
        launchInMinutes: minutesUntilLaunch,
        fbAdLiveStatus: "ACTIVE"
      }))
    };

    try {
      const response = await handleSaveAdLaunches(schedulePayload);
      if (response && response.batch_id) {
        const timeText =
          minutesUntilLaunch === 1
            ? "1 minute"
            : minutesUntilLaunch < 60
              ? `${minutesUntilLaunch} minutes`
              : `${Math.floor(minutesUntilLaunch / 60)} hour${Math.floor(minutesUntilLaunch / 60) !== 1 ? "s" : ""
              } and ${minutesUntilLaunch % 60} minute${minutesUntilLaunch % 60 !== 1 ? "s" : ""
              }`;
        navigate("/ads-progress", {
          state: {
            batchId: response.batch_id,
            launches: response.launches,
            isScheduled: true,
            scheduledTime: scheduledDateTime.toISOString(),
            scheduledUnixTime: Math.floor(scheduledDateTime.getTime() / 1000),
            launchInMinutes: minutesUntilLaunch,
            displayScheduledTime: timeText
          },
        });
        // notify(
        //   `Ads scheduled successfully for ${scheduledDateTime.toLocaleString()} (in ${timeText})`,
        //   "success"
        // );

        // Close modal and navigate to launch history
        setIsScheduleModalOpen(false);
      } else {
        notify("Failed to schedule ad launches. Please try again.", "error");
      }
    } catch (error) {
      console.error("Error during ad scheduling:", error);
      notify("An error occurred while scheduling ad launches", "error");
    } finally {
      setIsScheduleLoading(false);
    }
  };

  const getUniqueOptions = () => {
    const uniqueOptions = new Map();
    if (instagramPages && instagramPages.length > 0) {
      instagramPages.forEach(page => {
        if (page?.insta_id && page?.insta_page_name) {
          uniqueOptions.set(page.insta_id, {
            id: page.insta_id,
            displayName: page.insta_page_name
          });
        } else if (page?.insta_id && page?.name) {
          uniqueOptions.set(page.insta_id, {
            id: page.insta_id,
            displayName: `IGPage_${page.name}`
          });
        }
      });
    }
    const result = Array.from(uniqueOptions.values()).filter(option =>
      option.displayName && option.displayName.trim() !== ''
    );
    return result;
  };

  const handleToggleAdEnabled = (adId) => {
    setEnabledAds((prev) => ({
      ...prev,
      [adId]: prev[adId] === false ? true : false,
    }));
  };

  return (
    <>
      {(isDataLoading || createAdCreativeLoading || isValidating) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <Loader />
        </div>
      )}
      <div className="flex flex-col w-full min-h-screen gap-4 p-6 mx-auto bg-white max-w-7xl">
        <Navbar
          title="Review Default Values AP"
          onSaveAndNext={() => handleSaveAndPublish(true)}
          onSchedule={handleScheduleAds}
          isSaving={false}
          showBack={true}
          showSaveAndNext={true}
          showSchedule={true}
          saveButtonText="Save and Publish"
          scheduleButtonText="Schedule Ads"
          adAccountName={storedAdAccountName}
          loading={isScheduleModalOpen || isScheduleLoading}
        />
        <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="w-full mx-auto overflow-x-auto">
            <table className="w-full divide-y divide-gray-200" style={{ tableLayout: 'fixed' }}>
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase" style={{ width: '10%' }}>
                    Creative
                  </th>
                  <th scope="col" className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase" style={{ width: '15%' }}>
                    Ad Name
                  </th>
                  <th scope="col" className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase" style={{ width: '15%' }}>
                    Call to Action
                  </th>
                  <th scope="col" className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase" style={{ width: '17%' }}>
                    Deep Link
                  </th>
                  <th scope="col" className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase" style={{ width: '15%' }}>
                    Facebook Page
                  </th>
                  <th scope="col" className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase" style={{ width: '15%' }}>
                    Instagram Page
                  </th>
                  <th scope="col" className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase" style={{ width: '13%' }}>
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {ads?.map(
                  (ad) =>
                    ad.creatives.length > 0 && (
                      <tr key={ad._id}>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {ad.creatives?.length > 0 ? (
                            <img
                              src={ad.creatives[0].driveThumbnail || `https://drive.google.com/thumbnail?sz=w456-h200&id=${extractDriveId(
                                ad.creatives[0].fileLink
                              )}`}
                              alt={ad.creatives[0].fileName}
                              className="object-cover w-20 h-20 rounded"
                            />
                          ) : (
                            <div className="flex items-center justify-center w-16 h-16 bg-gray-200 rounded">
                              <span className="text-xs text-gray-500">
                                No creative
                              </span>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-normal break-words max-w-[12rem]" title={ad.name}>
                          <span className="block">
                            {ad.name}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex flex-col w-full gap-1">
                            <FormControl fullWidth size="small">
                              <SelectDropdown
                                name="callToAction"
                                value={reviewValues?.[ad?._id]?.callToAction?.id || ""}
                                onChange={(e) => handleReviewValuesChange(ad._id, e)}
                                options={ctaOptions || []}
                                optionValueKey="id"
                                optionLabelKey="text"
                                sx={{
                                  backgroundColor: "white",
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "rgb(209 213 219)",
                                  },
                                }}
                              />
                            </FormControl>
                            {ads?.indexOf(ad) === 0 && reviewValues?.[ad?._id]?.callToAction?.id && (
                              <button
                                onClick={() => {
                                  const selectedCTA = reviewValues?.[ad?._id]?.callToAction?.id;
                                  if (!selectedCTA) return;
                                  const updatedValues = { ...reviewValues };
                                  ads.forEach((adItem) => {
                                    if (adItem && updatedValues[adItem._id]) {
                                      updatedValues[adItem._id] = {
                                        ...updatedValues[adItem._id],
                                        callToAction: { id: selectedCTA },
                                      };
                                    }
                                  });
                                  setReviewValues(updatedValues);
                                }}
                                style={{ width: '100%' }}
                                className="flex items-center justify-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 transition-colors duration-200 border border-blue-200 rounded-md bg-blue-50 hover:bg-blue-100"
                              >
                                <Copy size={11} />
                                Apply to all
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex flex-col w-full gap-1">
                            <div className="flex items-center gap-1">
                              <TextField
                                variant="outlined"
                                size="small"
                                className="w-full"
                                placeholder="Enter deep link"
                                value={reviewValues?.[ad?._id]?.deepLink || ""}
                                onChange={(e) => handleReviewValuesChange(ad._id, e)}
                                name="deepLink"
                                InputProps={{
                                  style: {
                                    padding: "8px",
                                    borderColor: "rgb(209 213 219)",
                                  },
                                }}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                      borderColor: "rgb(209 213 219)",
                                    },
                                    "&:hover fieldset": {
                                      borderColor: "rgb(209 213 219)",
                                    },
                                    "&.Mui-focused fieldset": {
                                      borderColor: "blue",
                                    },
                                  },
                                }}
                              />
                            </div>
                            {ads?.indexOf(ad) === 0 && (
                              <button
                                onClick={() => {
                                  const deepLink = reviewValues?.[ad?._id]?.deepLink || "";
                                  const updatedValues = { ...reviewValues };
                                  ads.forEach((adItem) => {
                                    if (adItem && updatedValues[adItem._id]) {
                                      updatedValues[adItem._id] = {
                                        ...updatedValues[adItem._id],
                                        deepLink: deepLink,
                                      };
                                    }
                                  });
                                  setReviewValues(updatedValues);
                                }}
                                style={{ width: '100%' }}
                                className="flex items-center justify-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 transition-colors duration-200 border border-blue-200 rounded-md bg-blue-50 hover:bg-blue-100"
                              >
                                <Copy size={11} />
                                Apply to all
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex flex-col w-full">
                            <FormControl fullWidth size="small">
                              <SelectDropdown
                                name="facebookPage"
                                value={reviewValues?.[ad?._id]?.facebookPage?.id || ""}
                                onChange={(e) => handleReviewValuesChange(ad._id, e)}
                                options={facebookPages.map(page => ({ id: page.page_id, name: page.name }))}
                                optionValueKey="id"
                                optionLabelKey="name"
                                sx={{
                                  backgroundColor: "white",
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "rgb(209 213 219)",
                                  },
                                }}
                              />
                            </FormControl>
                            {ads?.indexOf(ad) === 0 && (
                              <button
                                onClick={() => {
                                  const selectedFbPage =
                                    reviewValues?.[ad?._id]?.facebookPage?.id;
                                  if (!selectedFbPage) return;
                                  const updatedValues = { ...reviewValues };
                                  ads.forEach((adItem) => {
                                    if (adItem && updatedValues[adItem._id]) {
                                      updatedValues[adItem._id] = {
                                        ...updatedValues[adItem._id],
                                        facebookPage: { id: selectedFbPage },
                                      };
                                    }
                                  });
                                  setReviewValues(updatedValues);
                                }}
                                style={{ width: '100%' }}
                                className="flex items-center justify-center gap-1 px-2 py-1 mt-1 text-xs font-medium text-blue-600 transition-colors duration-200 border border-blue-200 rounded-md bg-blue-50 hover:bg-blue-100"
                              >
                                <Copy size={11} />
                                Apply to all
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex flex-col w-full">
                            <FormControl fullWidth size="small">
                              <SelectDropdown
                                name="instagramPage"
                                value={reviewValues?.[ad?._id]?.instagramPage?.id || ""}
                                onChange={(e) => handleReviewValuesChange(ad._id, e)}
                                options={instagramPages.map(page => ({ id: page.insta_id, name: page.insta_page_name }))}
                                optionValueKey="id"
                                optionLabelKey="name"
                                sx={{
                                  backgroundColor: "white",
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "rgb(209 213 219)",
                                  },
                                }}
                              />
                            </FormControl>
                            {ads?.indexOf(ad) === 0 && (
                              <button
                                onClick={() => {
                                  const selectedInstaPage =
                                    reviewValues?.[ad?._id]?.instagramPage?.id;
                                  if (!selectedInstaPage) return;
                                  const updatedValues = { ...reviewValues };
                                  ads.forEach((adItem) => {
                                    if (adItem && updatedValues[adItem._id]) {
                                      updatedValues[adItem._id] = {
                                        ...updatedValues[adItem._id],
                                        instagramPage: { id: selectedInstaPage },
                                      };
                                    }
                                  });
                                  setReviewValues(updatedValues);
                                }}
                                style={{ width: '100%' }}
                                className="flex items-center justify-center gap-1 px-2 py-1 mt-1 text-xs font-medium text-blue-600 transition-colors duration-200 border border-blue-200 rounded-md bg-blue-50 hover:bg-blue-100"
                              >
                                <Copy size={11} />
                                Apply to all
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex flex-col w-full">
                            <div className="flex items-center gap-2 my-4">
                              <div
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none cursor-pointer ${enabledAds[ad._id] !== false ? 'bg-blue-600' : 'bg-gray-200'}`}
                                onClick={() => handleToggleAdEnabled(ad._id)}
                                role="switch"
                                aria-checked={enabledAds[ad._id] !== false}
                                tabIndex={0}
                              >
                                <span
                                  className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${enabledAds[ad._id] !== false ? 'translate-x-6' : 'translate-x-1'}`}
                                />
                              </div>
                              <span className="text-sm font-medium text-gray-700">
                                {enabledAds[ad._id] !== false ? "On" : "Off"}
                              </span>
                            </div>
                            {ads?.indexOf(ad) === 0 && (
                              <button
                                onClick={() => {
                                  const selectedStatus = enabledAds[ad._id] !== false;
                                  const updatedEnabledAds = { ...enabledAds };
                                  ads.forEach((adItem) => {
                                    if (adItem && adItem._id) {
                                      updatedEnabledAds[adItem._id] = selectedStatus;
                                    }
                                  });
                                  setEnabledAds(updatedEnabledAds);
                                }}
                                style={{ width: '100%' }}
                                className="flex items-center justify-center gap-1 px-2 py-1 mt-1 text-xs font-medium text-blue-600 transition-colors duration-200 border border-blue-200 rounded-md bg-blue-50 hover:bg-blue-100"
                              >
                                <Copy size={11} />
                                Apply to all
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {isScheduleModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="p-6 bg-white rounded-lg shadow-lg w-96">
            <h2 className="mb-4 text-xl font-semibold">Schedule Ads</h2>
            <div className="mb-4">
              <label htmlFor="scheduleDateTime" className="block mb-1 text-sm font-medium text-gray-700">
                Select date and time
              </label>
              <div className="flex justify-center">
                <input
                  id="scheduleDateTime"
                  type="datetime-local"
                  className="w-full max-w-xs px-3 py-2 mx-auto border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  defaultValue={(() => {
                    const date = new Date();
                    date.setMinutes(date.getMinutes());
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    const hours = String(date.getHours()).padStart(2, '0');
                    const minutes = String(date.getMinutes()).padStart(2, '0');
                    return `${year}-${month}-${day}T${hours}:${minutes}`;
                  })()}
                  min={(() => {
                    const date = new Date();
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    const hours = String(date.getHours()).padStart(2, '0');
                    const minutes = String(date.getMinutes()).padStart(2, '0');
                    return `${year}-${month}-${day}T${hours}:${minutes}`;
                  })()}
                  step="60"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setIsScheduleModalOpen(false);
                  notify("Scheduling cancelled", "info");
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                disabled={isScheduleLoading}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const dateTimeInput = document.getElementById('scheduleDateTime');
                  const selectedDateTime = new Date(dateTimeInput.value);
                  handleScheduleDateConfirm(selectedDateTime);
                }}
                className="flex items-center gap-2 px-4 py-2 text-white bg-green-600 rounded-md hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed"
                disabled={isScheduleLoading}
              >
                {isScheduleLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white rounded-full border-t-transparent animate-spin"></div>
                    Scheduling...
                  </>
                ) : (
                  'Schedule'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ad Posting Validation Modal */}
      {isValidationModalOpen && validationErrors.length > 0 && (
        <AdPostingValidation
          isOpen={isValidationModalOpen}
          onClose={() => setIsValidationModalOpen(false)}
          errors={validationErrors}
          onEditAds={handleValidationEditAds}
          onResolveErrors={handleValidationResolve}
        />
      )}
      {isPublishValidationModalOpen && publishValidationErrors.length > 0 && (
        <AdPostingValidation
          isOpen={isPublishValidationModalOpen}
          onClose={() => setIsPublishValidationModalOpen(false)}
          errors={publishValidationErrors}
          onEditAds={handlePublishValidationEditAds}
          onResolveErrors={handlePublishValidationResolve}
        />
      )}
    </>
  );
};

export default ReviewDefaultValuesAP;
